import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AppNavBar, setItemActive } from "baseui/app-nav-bar";
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Plus } from 'baseui/icon';
import { LuMenu } from 'react-icons/lu';
import Logout from '../Logout';
import { ProgressBar, SIZE } from "baseui/progress-bar";
import { LOGO_NAV_URL } from '../../brand/logo';
import { LEARN_HUB, LIBRARY, QNAREAD, STUDY_HUB } from '../../config/appLabels';
import { GAMIFICATION_NAV } from '../../config/gamificationNav';
import { NavRouteIcon } from './navIcons';
import AppMobileDrawer from '../layout/AppMobileDrawer';
import NavPrimaryMenuContainer from '../navigation/NavPrimaryMenuContainer';
import { NavBarUserMenuProvider } from '../navigation/NavUserMenu';
import NotificationBell from '../notifications/NotificationBell';
import { useTeamContextOptional } from '../../context/TeamContext';
import { useNotificationsOptional } from '../../context/NotificationContext';
import { useUserPlan } from '../../hooks/useUserPlan';
import { usePlanGate } from '../../hooks/usePlanGate';
import PlanTierLabel from '../billing/PlanTierLabel';
import { FEATURE } from '../../config/planFeatures';
import './styles/Navbar.css';

/** Tablet / phone: always Menu drawer + bell (no horizontal nav). */
export const APP_NAV_COMPACT_MAX_PX = 900;

const NAV_BRAND_MENU_MIN_GAP_PX = 16;

/** Desktop top nav label density. */
function resolveNavLabelMode(width) {
  if (width <= APP_NAV_COMPACT_MAX_PX) return 'hidden';
  if (width <= 1140) return 'icons';
  if (width <= 1920) return 'short';
  return 'full';
}

function detectNavBrandCollision(navInnerEl) {
  if (!navInnerEl) return false;
  const brand = navInnerEl.querySelector('.navbar-brand');
  const primary = navInnerEl.querySelector('.app-nav-primary-menu');
  const desktopMenu = navInnerEl.querySelector('.app-nav-desktop-menu');
  if (!brand || !primary || !desktopMenu) return false;
  if (window.getComputedStyle(primary).display === 'none') return false;

  const brandRect = brand.getBoundingClientRect();
  const primaryRect = primary.getBoundingClientRect();
  const desktopRect = desktopMenu.getBoundingClientRect();
  const gap = primaryRect.left - brandRect.right;
  const overlap = brandRect.right > primaryRect.left - 2;
  const tight = gap < NAV_BRAND_MENU_MIN_GAP_PX;
  const menuOverflow = primary.scrollWidth > primary.clientWidth + 2;
  const offScreen = desktopRect.right > window.innerWidth - 6;
  return overlap || tight || menuOverflow || offScreen;
}

const Navbar = ({ userProfile, loading }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const teamCtx = useTeamContextOptional();
  const { canAccessTeams, canUseFeature } = useUserPlan();
  const { navigateIfAllowed } = usePlanGate();
  const pendingTeamRequests = teamCtx?.pendingRequestCount ?? 0;
  const notifCtx = useNotificationsOptional();
  const unreadNotifications = notifCtx?.unreadCount ?? 0;
  const [value, setValue] = useState(10);
  const [navbarVisible, setNavbarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showEmailSubtitle, setShowEmailSubtitle] = useState(
      () => typeof window !== 'undefined' && window.innerWidth >= 640
  );
  const [navLabelMode, setNavLabelMode] = useState(
      () => (typeof window !== 'undefined' ? resolveNavLabelMode(window.innerWidth) : 'full')
  );
  const [navCollides, setNavCollides] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
      () => (typeof window !== 'undefined' ? window.innerWidth : 1200)
  );
  const navInnerRef = useRef(null);
  const lastProbeWidthRef = useRef(0);

  const showCompactNav = navLabelMode === 'hidden' || navCollides;

  const userImgUrl = useMemo(() => {
    const name = userProfile?.fullName || userProfile?.email || 'U';
    const letter = String(name).trim().charAt(0).toUpperCase() || 'U';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect width="80" height="80" rx="40" fill="#2563eb"/><text x="50%" y="56%" text-anchor="middle" font-size="36" fill="white" font-family="system-ui,sans-serif">${letter}</text></svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }, [userProfile]);

  const hasAdminRole = useMemo(() => {
    if (!userProfile) return false;
    const roles = userProfile?.roles || [];
    if (roles.includes('ROLE_ADMIN') || roles.includes('ROLE_SYSTEM_ADMIN')) return true;
    return userProfile?.authorities?.some(
      (auth) => auth?.authority === 'ROLE_ADMIN' || auth?.authority === 'ROLE_SYSTEM_ADMIN',
    ) || false;
  }, [userProfile]);

  const navCommonItems = useMemo(() => [
    {
      index: 1,
      label: STUDY_HUB.navLabel || STUDY_HUB.name,
      navLabel: STUDY_HUB.navLabel || STUDY_HUB.name,
      shortNavLabel: STUDY_HUB.shortNavLabel,
      icon: Home,
      path: STUDY_HUB.path,
    },
    {
      index: 10,
      label: LEARN_HUB.name,
      navLabel: LEARN_HUB.navLabel,
      shortNavLabel: LEARN_HUB.shortNavLabel,
      icon: Home,
      path: LEARN_HUB.path,
      learningCore: true,
    },
    { index: 2, label: LIBRARY.name, navLabel: LIBRARY.name, shortNavLabel: LIBRARY.name, icon: Home, path: LIBRARY.path },
    { index: 6, label: 'Saved', navLabel: 'Saved', shortNavLabel: 'Saved', icon: Plus, path: '/saved', learningCore: true },
    { index: 7, label: 'Practice', navLabel: 'Practice', shortNavLabel: 'Practice', icon: Plus, path: '/practice', learningCore: true },
    { index: 8, label: 'Review', navLabel: 'Review', shortNavLabel: 'Review', icon: Plus, path: '/review', learningCore: true },
    {
      index: 9,
      label: GAMIFICATION_NAV.profile.shortLabel,
      navLabel: GAMIFICATION_NAV.profile.shortLabel,
      shortNavLabel: GAMIFICATION_NAV.profile.shortLabel,
      icon: Plus,
      path: GAMIFICATION_NAV.profile.path,
    },
    {
      index: 4,
      label: GAMIFICATION_NAV.leaderboard.label,
      navLabel: GAMIFICATION_NAV.leaderboard.label,
      shortNavLabel: GAMIFICATION_NAV.leaderboard.shortLabel,
      icon: Plus,
      path: GAMIFICATION_NAV.leaderboard.path,
      planTier: 'Pro',
    },
    { index: 3, label: 'Teams', navLabel: 'Teams', shortNavLabel: 'Teams', icon: Plus, path: '/teams', planTier: 'Team' },
  ], []);

  const commonItems = navCommonItems;

  const adminItems = useMemo(() => [
    { label: 'Admin console', path: '/admin' },
  ], []);

  const [mainItems, setMainItems] = useState(commonItems);

  useEffect(() => {
    setMainItems(commonItems);
  }, [commonItems]);

  const isNavItemActive = (itemPath, path) => {
    if (!itemPath) return false;
    if (itemPath === STUDY_HUB.path) return path === STUDY_HUB.path;
    if (itemPath === LIBRARY.path) {
      return path === LIBRARY.path || path === '/home' || path === '/';
    }
    if (itemPath === LEARN_HUB.path) return path === LEARN_HUB.path || path.startsWith(`${LEARN_HUB.path}/`);
    if (itemPath === '/teams') return path.startsWith('/teams');
    if (itemPath === GAMIFICATION_NAV.profile.path) return path === itemPath || path.startsWith(`${itemPath}/`);
    if (itemPath === GAMIFICATION_NAV.leaderboard.path) return path === itemPath || path.startsWith(`${itemPath}/`);
    if (itemPath === '/admin') return path.startsWith('/admin');
    return path === itemPath || path.startsWith(`${itemPath}/`);
  };

  useEffect(() => {
    const path = location.pathname;
    setMainItems((prev) =>
      prev.map((item) => ({
        ...item,
        active: isNavItemActive(item.path, path),
      }))
    );
  }, [location.pathname]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const userItems = useMemo(() => {
    const items = [
      { label: 'Progress', sectionHeader: true },
      { label: GAMIFICATION_NAV.profile.shortLabel, path: GAMIFICATION_NAV.profile.path },
      { label: GAMIFICATION_NAV.leaderboard.shortLabel, path: GAMIFICATION_NAV.leaderboard.path },
      { label: 'Account', sectionHeader: true },
      { label: 'Settings', path: '/settings' },
      { label: 'Question editor', path: '/settings?tab=manage' },
    ];
    if (hasAdminRole) {
      items.push(
        { label: 'Admin', sectionHeader: true },
        { label: 'Admin console', path: '/admin' },
      );
    }
    items.push({ label: <Logout />, class: 1 });
    return items;
  }, [hasAdminRole]);

  const handleNavigation = (path) => {
    if (!path) return;
    sessionStorage.setItem("lastVisitedPage", path);
    navigateIfAllowed(path);
  };

  const handleUserItemSelect = (item) => {
    if (item?.sectionHeader || !item?.path) return;
    handleNavigation(item.path);
  };

  const handleMainItemSelect = (item) => {
    setMainItems((prev) => setItemActive(prev, item));
    handleNavigation(item.path);
  };

  const longNavLabel = (item) => item.navLabel || (typeof item.label === 'string' ? item.label : '');
  const shortNavLabel = (item) => item.shortNavLabel || longNavLabel(item);

  const visibleNavLabel = (item) => {
    if (navLabelMode === 'icons') return null;
    if (navLabelMode === 'short') return shortNavLabel(item);
    return longNavLabel(item);
  };

  const mapItemToNode = (item) => {
    if (React.isValidElement(item.label)) {
      return item.label;
    }
    if (item.sectionHeader) {
      return <span className="profile-menu-section-title">{item.label}</span>;
    }
    if (item.path && item.index === undefined) {
      return (
        <span className="profile-menu-link">
          <NavRouteIcon path={item.path} className="profile-menu-link-icon" />
          <span className="profile-menu-link-text">{item.label}</span>
          {item.path === GAMIFICATION_NAV.leaderboard.path
            && !canUseFeature(FEATURE.LEADERBOARD_RANK) && <PlanTierLabel tier="Pro" />}
        </span>
      );
    }
    const fullTitle = longNavLabel(item);
    const text = visibleNavLabel(item);
    const icon = <NavRouteIcon path={item.path} />;
    const densityClass = navLabelMode === 'icons'
      ? ' app-nav-link--icons-only'
      : navLabelMode === 'short'
        ? ' app-nav-link--short-label'
        : '';
    const activeClass = item.active ? ' app-nav-link--active' : '';
    if (item.learningCore) {
      return (
        <span
          className={`app-nav-learning-link${densityClass}${activeClass}`}
          title={fullTitle}
          aria-label={navLabelMode === 'icons' ? fullTitle : undefined}
        >
          {icon}
          {text}
        </span>
      );
    }
    return (
      <span
        className={`app-nav-item-label${densityClass}${activeClass}`}
        title={fullTitle}
        aria-label={navLabelMode === 'icons' ? fullTitle : undefined}
      >
        {icon}
        {text}
        {item.planTier === 'Team' && !canAccessTeams && <PlanTierLabel tier="Team" />}
        {item.path === GAMIFICATION_NAV.leaderboard.path
          && !canUseFeature(FEATURE.LEADERBOARD_RANK) && <PlanTierLabel tier="Pro" />}
        {item.path === '/teams' && pendingTeamRequests > 0 && (
          <span className="app-nav-badge" aria-label={`${pendingTeamRequests} pending team requests`}>
            {pendingTeamRequests > 9 ? '9+' : pendingTeamRequests}
          </span>
        )}
      </span>
    );
  };

  const navUserMenuContext = useMemo(
    () => ({
      userItems,
      userProfile,
      userImgUrl,
      onUserItemSelect: handleUserItemSelect,
      renderItem: (item) => mapItemToNode(item),
    }),
    [userItems, userProfile, userImgUrl, canUseFeature, navLabelMode, pendingTeamRequests, canAccessTeams],
  );

  useEffect(() => {
    if (!loading) return;
    const interval = setInterval(() => {
      setValue((prevValue) => (prevValue < 100 ? prevValue + 10 : 100));
    }, 200);
    return () => clearInterval(interval);
  }, [loading]);

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setViewportWidth(w);
      setShowEmailSubtitle(w >= 640);
      setNavLabelMode(resolveNavLabelMode(w));
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useLayoutEffect(() => {
    const shell = navInnerRef.current;
    if (!shell) return undefined;

    const probe = () => {
      const w = window.innerWidth;
      if (w <= APP_NAV_COMPACT_MAX_PX) {
        setNavCollides(false);
        return;
      }

      const primary = shell.querySelector('.app-nav-primary-menu');
      const primaryHidden = !primary || window.getComputedStyle(primary).display === 'none';

      if (primaryHidden) {
        setNavCollides((prev) => {
          if (!prev) return false;
          if (w > lastProbeWidthRef.current + 40) {
            lastProbeWidthRef.current = w;
            return false;
          }
          return prev;
        });
        return;
      }

      lastProbeWidthRef.current = w;
      setNavCollides(detectNavBrandCollision(shell));
    };

    probe();
    const ro = new ResizeObserver(probe);
    ro.observe(shell);
    return () => ro.disconnect();
  }, [viewportWidth, navLabelMode, location.pathname, mainItems.length, showCompactNav]);

  useEffect(() => {
    const handleScroll = () => {
      setLastScrollY(window.scrollY);
      if (window.scrollY > lastScrollY && window.scrollY > 50) {
        setNavbarVisible(false);
      } else {
        setNavbarVisible(true);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
      <>
      <AppMobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        items={mainItems}
        adminItems={hasAdminRole ? adminItems : []}
        userProfile={userProfile}
        pendingTeamRequests={pendingTeamRequests}
        unreadNotifications={unreadNotifications}
      />
      <div
          className={[
            'app-navbar-fixed',
            'app-navbar-shell',
            showCompactNav && 'app-nav-compact',
            navLabelMode === 'short' && 'app-nav-labels-short',
            navLabelMode === 'icons' && 'app-nav-labels-icons',
          ].filter(Boolean).join(' ')}
          style={{
            top: navbarVisible ? 0 : '-100px',
          }}
      >
        <div className="app-navbar-inner" ref={navInnerRef}>
          <NavBarUserMenuProvider value={navUserMenuContext}>
          <AppNavBar
              title={
                <button
                  type="button"
                  className="navbar-brand app-nav-brand-mark"
                  onClick={() => handleNavigation('/dashboard')}
                  aria-label="QnaRead home"
                >
                  <img
                    src={LOGO_NAV_URL}
                    alt="QnaRead logo"
                    className="navbar-brand-logo"
                    width={40}
                    height={40}
                  />
                  <span className="navbar-brand-copy">
                    <span className="navbar-brand-wordmark">
                      <span className="navbar-brand-qna">QnA</span>
                      <span className="navbar-brand-read">Read</span>
                    </span>
                    <span className="navbar-brand-tagline">{QNAREAD.taglineShort}</span>
                  </span>
                </button>
              }
              mainItems={showCompactNav ? [] : mainItems}
              userItems={[]}
              mapItemToNode={mapItemToNode}
              onMainItemSelect={handleMainItemSelect}
              username=""
              userImgUrl=""
              overrides={{
                SideMenuButton: {
                  component: () => null,
                },
                MobileDrawer: {
                  style: {
                    display: 'none',
                  },
                },
                Root: {
                  style: {
                    position: 'relative',
                    height: 'auto',
                    paddingBottom: 0,
                    borderBottom: '1px solid #e2e8f0',
                  },
                },
                AppName: {
                  className: 'app-nav-brand-slot',
                  style: {
                    flex: '0 0 auto',
                    minWidth: 0,
                    maxWidth: 'none',
                    overflow: 'visible',
                  },
                },
                DesktopMenuContainer: {
                  style: {
                    width: '100%',
                  },
                },
                DesktopMenu: {
                  className: 'app-nav-desktop-menu',
                  style: {
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 0,
                    width: '100%',
                    maxWidth: 'none',
                    marginLeft: 0,
                    marginRight: 0,
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    paddingBlockStart: '10px',
                    paddingBlockEnd: '10px',
                    boxSizing: 'border-box',
                  },
                },
                PrimaryMenuContainer: {
                  component: NavPrimaryMenuContainer,
                  className: 'app-nav-primary-menu app-nav-main app-nav-main--learning app-nav-desktop-only',
                  style: {
                    flex: '1 1 auto',
                    minWidth: 0,
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: '8px',
                  },
                },
                MainMenuItem: {
                  style: ({ $active }) => ({
                    flexShrink: 0,
                    marginLeft: navLabelMode === 'icons' ? '2px' : navLabelMode === 'short' ? '4px' : '6px',
                    marginRight: navLabelMode === 'icons' ? '2px' : navLabelMode === 'short' ? '4px' : '6px',
                    paddingLeft: navLabelMode === 'icons' ? '8px' : navLabelMode === 'short' ? '8px' : '12px',
                    paddingRight: navLabelMode === 'icons' ? '8px' : navLabelMode === 'short' ? '8px' : '12px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    whiteSpace: 'nowrap',
                    fontSize: navLabelMode === 'short' ? '14px' : '15px',
                    lineHeight: '1.35',
                    borderBottomWidth: '0',
                    borderRadius: '8px',
                    backgroundColor: $active ? '#2563eb' : 'transparent',
                    boxShadow: $active ? '0 1px 3px rgba(37, 99, 235, 0.35)' : 'none',
                    transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                  }),
                },
              }}
          />
          </NavBarUserMenuProvider>
          <div className="app-nav-menu-actions">
            {userProfile && (
              <div className="app-nav-notifications-mobile">
                <NotificationBell />
              </div>
            )}
            <button
              type="button"
              className="app-nav-mobile-toggle"
              aria-expanded={drawerOpen}
              aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setDrawerOpen((open) => !open)}
            >
              <LuMenu className="app-nav-mobile-toggle-icon" aria-hidden />
              <span className="app-nav-mobile-toggle-text">Menu</span>
            </button>
          </div>
          {loading && (
              <ProgressBar
                  value={value}
                  size={SIZE.small}
                  overrides={{
                    Root: {
                      style: {
                        position: 'absolute',
                        bottom: 0,
                        left: '-13px',
                        right: 0,
                        height: '14px',
                        margin: 0,
                        width: '108%',
                      },
                    },
                  }}
              />
          )}
        </div>
      </div>
      </>
  );
};

export default Navbar;
